import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ORIGINAL_RENDITION, timelineGroupsFromBlock, } from "@diality/contracts";
import * as Dialog from "@radix-ui/react-dialog";
import * as React from "react";
import { PAGE_GUTTER, Section } from "./_section";
import { TimelineScrollbar } from "./timeline-scrollbar";
import { ArrowNav } from "../components/ui/arrow-nav";
import { cn } from "../lib/utils";
/* --- Motion constants, carried over from the prototype ------------------- */
// Run the existing movement at 1.125× speed, preserving its easing and travel.
const IMAGE_MOTION_SPEED = 1.125;
const INERTIA_IMPULSE_RATIO = 0.6;
/** Outer cards need a larger travel multiplier to read as a distinct layer. */
const MILESTONE_INERTIA_IMPULSE_RATIO = 1.6;
const INERTIA_LIMIT = 800;
const INERTIA_EASE = 0.09;
const MILESTONE_INERTIA_EASE = 0.05;
/** Added per visible milestone from trailing to leading after year navigation. */
const DIRECTION_EASE_OFFSET = 0.04;
const MILESTONE_DIRECTION_EASE_OFFSET = 0.06;
const IMAGE_INERTIA_SETTLE_DELAY = 100 / IMAGE_MOTION_SPEED;
/**
 * One animation frame gives the outer layer time to absorb fresh input without
 * creating a perceptible hold before it begins its return to center.
 */
const MILESTONE_INPUT_WINDOW = 34 / IMAGE_MOTION_SPEED;
const SNAP_DELAY = 60 / IMAGE_MOTION_SPEED;
const NAVIGATE_DURATION = 700 / IMAGE_MOTION_SPEED;
const SNAP_DURATION = 480 / IMAGE_MOTION_SPEED;
const ODOMETER_DIGIT_DELAY = 55;
/** Stand-ins for the prototype's GSAP eases: power3.inOut, power2/3.out. */
const easeInOutCubic = (progress) => progress < 0.5 ? 4 * progress ** 3 : 1 - (-2 * progress + 2) ** 3 / 2;
const easeOutQuad = (progress) => 1 - (1 - progress) ** 2;
const prefersReducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;
/**
 * A rAF tween over a scalar, standing in for `gsap.to`. Returns a canceller so
 * the next call can overwrite the one in flight.
 */
function tween(from, to, duration, ease, onUpdate, onComplete) {
    let frame = 0;
    const startedAt = performance.now();
    const step = (now) => {
        const progress = Math.min(1, (now - startedAt) / duration);
        onUpdate(from + (to - from) * ease(progress));
        if (progress < 1) {
            frame = window.requestAnimationFrame(step);
        }
        else {
            frame = 0;
            onComplete?.();
        }
    };
    frame = window.requestAnimationFrame(step);
    return () => {
        if (frame)
            window.cancelAnimationFrame(frame);
    };
}
function LightboxImage({ src, alt }) {
    const [displayed, setDisplayed] = React.useState({ src, alt });
    const imageRef = React.useRef(null);
    React.useLayoutEffect(() => {
        const image = imageRef.current;
        if (!image)
            return;
        if (prefersReducedMotion()) {
            setDisplayed({ src, alt });
            image.style.opacity = "1";
            return;
        }
        let cancel;
        const fade = (opacity, onComplete) => {
            cancel?.();
            cancel = tween(Number(image.style.opacity || 1), opacity, 180, easeInOutCubic, (value) => {
                image.style.opacity = String(value);
            }, onComplete);
        };
        const reveal = () => fade(1);
        if (displayed.src !== src) {
            // Replace the only image once it is fully transparent; never overlap images.
            fade(0, () => setDisplayed({ src, alt }));
        }
        else if (image.complete) {
            reveal();
        }
        else {
            image.addEventListener("load", reveal);
            image.addEventListener("error", reveal);
        }
        return () => {
            cancel?.();
            image.removeEventListener("load", reveal);
            image.removeEventListener("error", reveal);
        };
    }, [src, alt, displayed.src]);
    return (_jsx("img", { ref: imageRef, className: "pointer-events-auto block h-auto w-auto min-h-0 max-h-full max-w-full object-contain", src: displayed.src, alt: displayed.src === src ? alt : displayed.alt, draggable: false, "data-timeline-lightbox-image": true }));
}
function OdometerYear({ year }) {
    const [displayedYear, setDisplayedYear] = React.useState(year);
    const [previousYear, setPreviousYear] = React.useState(year);
    React.useEffect(() => {
        if (year === displayedYear)
            return;
        setPreviousYear(displayedYear);
        setDisplayedYear(year);
    }, [displayedYear, year]);
    return (_jsx("span", { "aria-label": displayedYear, className: "flex", children: [...displayedYear].map((digit, index) => {
            const previousDigit = previousYear[index] ?? digit;
            return (_jsx("span", { "aria-hidden": "true", className: "inline-block h-[1.04em] w-[0.55em] shrink-0 overflow-hidden text-center", children: _jsxs("span", { className: cn("flex flex-col", previousDigit !== digit &&
                        "animate-odometer-roll motion-reduce:animate-none motion-reduce:-translate-y-1/2"), style: { animationDelay: `${index * ODOMETER_DIGIT_DELAY}ms` }, children: [_jsx("span", { className: "block h-[1.04em]", children: previousDigit }), _jsx("span", { className: "block h-[1.04em]", children: digit })] }, `${previousDigit}-${digit}`) }, index));
        }) }));
}
/** A sliding image rail with stationary navigation, year and copy. */
export function Timeline({ milestones, eyebrow = "Company Milestones", className, timelineTitle = "Our Story", }) {
    const viewportRef = React.useRef(null);
    const viewportWidthRef = React.useRef(0);
    const railRef = React.useRef(null);
    const milestoneRefs = React.useRef([]);
    /** Image frames define the rest stops independently of animated transforms. */
    const detailRefs = React.useRef([]);
    /** The independently animated inner image layer. */
    const imageMotionRefs = React.useRef([]);
    const cancelNavigationRef = React.useRef(null);
    const dragStartRef = React.useRef(null);
    const snapTimerRef = React.useRef(null);
    const scrollbarDraggingRef = React.useRef(false);
    const motionRef = React.useRef({
        frame: 0,
        lastInput: 0,
        lastScroll: 0,
        visible: [],
        imageValues: Array(milestones.length).fill(0),
        imageTargets: Array(milestones.length).fill(0),
        imageEases: Array(milestones.length).fill(INERTIA_EASE),
        milestoneValues: Array(milestones.length).fill(0),
        milestoneTargets: Array(milestones.length).fill(0),
        milestoneEases: Array(milestones.length).fill(MILESTONE_INERTIA_EASE),
        isNavigating: false,
    });
    const [activeMilestone, setActiveMilestone] = React.useState(0);
    const activeMilestoneRef = React.useRef(0);
    const [isDragging, setIsDragging] = React.useState(false);
    const draggedRef = React.useRef(false);
    const galleryTriggerRef = React.useRef(null);
    const navigationUnlockTimerRef = React.useRef(null);
    const navigationLockedRef = React.useRef(false);
    const [navigationLocked, setNavigationLocked] = React.useState(false);
    const [lightbox, setLightbox] = React.useState(null);
    const lightboxSwipeRef = React.useRef(null);
    const lightboxDraggedRef = React.useRef(false);
    const [copyIndex, setCopyIndex] = React.useState(0);
    const [copyVisible, setCopyVisible] = React.useState(true);
    const copyRef = React.useRef(null);
    const [copyHeight, setCopyHeight] = React.useState(0);
    React.useEffect(() => {
        if (copyIndex === activeMilestone) {
            setCopyVisible(true);
            return;
        }
        if (prefersReducedMotion()) {
            setCopyIndex(activeMilestone);
            setCopyVisible(true);
            return;
        }
        setCopyVisible(false);
        const timer = window.setTimeout(() => {
            setCopyIndex(activeMilestone);
            setCopyVisible(true);
        }, 180);
        return () => window.clearTimeout(timer);
    }, [activeMilestone, copyIndex]);
    // Reserve the tallest copy's height, so arrows never jump between groups.
    React.useLayoutEffect(() => {
        const node = copyRef.current;
        if (!node)
            return;
        const measure = () => setCopyHeight(Math.max(0, ...Array.from(node.children).map((child) => child.getBoundingClientRect().height)));
        measure();
        const observer = new ResizeObserver(measure);
        Array.from(node.children).forEach((child) => observer.observe(child));
        return () => observer.disconnect();
    }, [milestones]);
    // Milestones arrive from the CMS, so the per-card motion arrays are sized on
    // every count change rather than once on mount.
    React.useLayoutEffect(() => {
        const cards = milestones.length;
        const motion = motionRef.current;
        if (motion.imageValues.length === cards)
            return;
        motion.visible = [];
        motion.imageValues = Array(cards).fill(0);
        motion.imageTargets = Array(cards).fill(0);
        motion.imageEases = Array(cards).fill(INERTIA_EASE);
        motion.milestoneValues = Array(cards).fill(0);
        motion.milestoneTargets = Array(cards).fill(0);
        motion.milestoneEases = Array(cards).fill(MILESTONE_INERTIA_EASE);
        milestoneRefs.current.length = cards;
        detailRefs.current.length = cards;
        imageMotionRefs.current.length = cards;
    }, [milestones.length]);
    /**
     * Centre the image frame, excluding the rail spacing around each group.
     */
    const detailStop = (index) => {
        const milestone = milestoneRefs.current[index];
        const detail = detailRefs.current[index];
        const viewport = viewportRef.current;
        if (!milestone || !viewport)
            return 0;
        const detailCenter = detail
            ? detail.offsetLeft + detail.offsetWidth / 2
            : milestone.offsetWidth / 2;
        return milestone.offsetLeft + detailCenter - viewport.clientWidth / 2;
    };
    const syncRailEndPadding = () => {
        const viewport = viewportRef.current;
        const rail = railRef.current;
        const firstMilestone = milestoneRefs.current[0];
        const lastMilestone = milestoneRefs.current.at(-1);
        if (!viewport || !rail || !firstMilestone || !lastMilestone)
            return;
        const firstDetail = detailRefs.current[0];
        const lastDetail = detailRefs.current.at(-1);
        const firstDetailCenter = firstDetail
            ? firstDetail.offsetLeft + firstDetail.offsetWidth / 2
            : firstMilestone.offsetWidth / 2;
        const lastDetailCenter = lastDetail
            ? lastDetail.offsetLeft + lastDetail.offsetWidth / 2
            : lastMilestone.offsetWidth / 2;
        // Allow both the first and last image frames to settle at viewport centre.
        rail.style.paddingLeft = `${Math.max(0, viewport.clientWidth / 2 - firstDetailCenter)}px`;
        rail.style.paddingRight = `${Math.max(0, viewport.clientWidth / 2 - (lastMilestone.offsetWidth - lastDetailCenter))}px`;
    };
    const visibleMilestonesAt = (scrollLeft) => {
        const viewport = viewportRef.current;
        if (!viewport)
            return [];
        const viewportEnd = scrollLeft + viewport.clientWidth;
        return milestoneRefs.current.flatMap((milestone, index) => milestone &&
            milestone.offsetLeft + milestone.offsetWidth > scrollLeft &&
            milestone.offsetLeft < viewportEnd
            ? [index]
            : []);
    };
    const resetInertiaEases = () => {
        const motion = motionRef.current;
        motion.imageEases.fill(INERTIA_EASE);
        motion.milestoneEases.fill(MILESTONE_INERTIA_EASE);
    };
    const setDirectionalInertiaEases = (destination, direction) => {
        const motion = motionRef.current;
        const visible = visibleMilestonesAt(destination);
        resetInertiaEases();
        // The rail itself travels opposite to scrollLeft: a move toward later
        // years brings content in from the right, so the leftmost landing item
        // leads; moving toward earlier years reverses that order. The number of
        // layers comes from the rendered destination viewport, not an assumed
        // count of three.
        const ordered = direction === 1 ? visible : [...visible].reverse();
        ordered.forEach((index, order) => {
            motion.imageEases[index] =
                INERTIA_EASE + (ordered.length - 1 - order) * DIRECTION_EASE_OFFSET;
            motion.milestoneEases[index] =
                MILESTONE_INERTIA_EASE +
                    (ordered.length - 1 - order) * MILESTONE_DIRECTION_EASE_OFFSET;
        });
    };
    const driveInertia = (distance, force = false) => {
        if (scrollbarDraggingRef.current ||
            (!distance && !force) ||
            prefersReducedMotion())
            return;
        const viewport = viewportRef.current;
        if (!viewport)
            return;
        const motion = motionRef.current;
        // Rect-based, so a milestone already carrying an impulse still counts as
        // visible while it travels.
        const viewportBounds = viewport.getBoundingClientRect();
        motion.visible = milestoneRefs.current.flatMap((milestone, index) => {
            if (!milestone)
                return [];
            const bounds = milestone.getBoundingClientRect();
            return bounds.right > viewportBounds.left &&
                bounds.left < viewportBounds.right
                ? [index]
                : [];
        });
        const clamp = (value) => Math.max(-INERTIA_LIMIT, Math.min(INERTIA_LIMIT, value));
        motion.visible.forEach((index) => {
            motion.imageTargets[index] = clamp(distance * INERTIA_IMPULSE_RATIO);
            motion.milestoneTargets[index] = clamp(distance * MILESTONE_INERTIA_IMPULSE_RATIO);
        });
        motion.lastInput = performance.now();
        if (motion.frame)
            return;
        const advance = () => {
            const elapsed = performance.now() - motion.lastInput;
            let needsAnotherFrame = false;
            motion.imageValues.forEach((_value, index) => {
                const isVisible = motion.visible.includes(index);
                const imageTarget = isVisible &&
                    (motion.isNavigating || elapsed < IMAGE_INERTIA_SETTLE_DELAY)
                    ? motion.imageTargets[index]
                    : 0;
                const nextImage = motion.imageValues[index] +
                    (imageTarget - motion.imageValues[index]) *
                        (1 - (1 - motion.imageEases[index]) ** IMAGE_MOTION_SPEED);
                motion.imageValues[index] =
                    Math.abs(imageTarget - nextImage) < 0.03 ? imageTarget : nextImage;
                if (!isVisible && motion.imageValues[index] === 0) {
                    motion.imageTargets[index] = 0;
                }
                const milestoneTarget = isVisible && (motion.isNavigating || elapsed < MILESTONE_INPUT_WINDOW)
                    ? motion.milestoneTargets[index]
                    : 0;
                const nextMilestone = motion.milestoneValues[index] +
                    (milestoneTarget - motion.milestoneValues[index]) *
                        (1 - (1 - motion.milestoneEases[index]) ** IMAGE_MOTION_SPEED);
                motion.milestoneValues[index] =
                    Math.abs(milestoneTarget - nextMilestone) < 0.03
                        ? milestoneTarget
                        : nextMilestone;
                if (!isVisible && motion.milestoneValues[index] === 0) {
                    motion.milestoneTargets[index] = 0;
                }
                const image = imageMotionRefs.current[index];
                const milestone = milestoneRefs.current[index];
                if (image) {
                    image.style.transform = `translate3d(${motion.imageValues[index].toFixed(2)}px, 0, 0)`;
                }
                // Preserve the original outer image layer and its directional stagger.
                if (milestone) {
                    milestone.style.transform = `translate3d(${motion.milestoneValues[index].toFixed(2)}px, 0, 0)`;
                }
                needsAnotherFrame ||=
                    Math.abs(imageTarget - motion.imageValues[index]) > 0.03;
                needsAnotherFrame ||=
                    Math.abs(milestoneTarget - motion.milestoneValues[index]) > 0.03;
            });
            if (motion.isNavigating ||
                needsAnotherFrame ||
                elapsed < IMAGE_INERTIA_SETTLE_DELAY) {
                motion.frame = window.requestAnimationFrame(advance);
            }
            else {
                motion.imageValues.fill(0);
                motion.imageTargets.fill(0);
                motion.milestoneValues.fill(0);
                motion.milestoneTargets.fill(0);
                resetInertiaEases();
                motion.frame = 0;
            }
        };
        motion.frame = window.requestAnimationFrame(advance);
    };
    const syncTimelinePosition = () => {
        const viewport = viewportRef.current;
        if (!viewport)
            return;
        const resized = viewportWidthRef.current !== viewport.clientWidth;
        viewportWidthRef.current = viewport.clientWidth;
        syncRailEndPadding();
        // A browser can clamp scrollLeft before ResizeObserver runs. Keep the
        // selected frame when reflow changes its rest stop instead of treating
        // that automatic scroll as navigation to another milestone.
        if (resized && !dragStartRef.current && !scrollbarDraggingRef.current) {
            interruptNavigation();
            viewport.scrollLeft = Math.max(0, detailStop(activeMilestoneRef.current));
        }
        const nextActive = milestones.reduce((active, _milestone, index) => Math.abs(detailStop(index) - viewport.scrollLeft) <
            Math.abs(detailStop(active) - viewport.scrollLeft)
            ? index
            : active, 0);
        if (nextActive !== activeMilestoneRef.current) {
            activeMilestoneRef.current = nextActive;
            setActiveMilestone(nextActive);
        }
        const motion = motionRef.current;
        const distance = viewport.scrollLeft - motion.lastScroll;
        motion.lastScroll = viewport.scrollLeft;
        driveInertia(distance);
    };
    const moveTimelineTo = (destination, options = {}) => {
        const viewport = viewportRef.current;
        if (!viewport)
            return;
        cancelNavigationRef.current?.();
        const maximum = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
        const next = Math.max(0, Math.min(maximum, destination));
        const direction = next < viewport.scrollLeft ? -1 : 1;
        const motion = motionRef.current;
        motion.isNavigating = true;
        const duration = prefersReducedMotion()
            ? 1
            : (options.duration ?? NAVIGATE_DURATION);
        if (navigationUnlockTimerRef.current !== null) {
            window.clearTimeout(navigationUnlockTimerRef.current);
        }
        navigationUnlockTimerRef.current = window.setTimeout(() => {
            navigationUnlockTimerRef.current = null;
            navigationLockedRef.current = false;
            setNavigationLocked(false);
        }, duration);
        navigationLockedRef.current = true;
        setNavigationLocked(true);
        setDirectionalInertiaEases(next, direction);
        cancelNavigationRef.current = tween(viewport.scrollLeft, next, duration, options.ease ?? easeInOutCubic, (value) => {
            viewport.scrollLeft = value;
        }, () => {
            cancelNavigationRef.current = null;
            syncTimelinePosition();
            motion.isNavigating = false;
            motion.visible = visibleMilestonesAt(next);
            // Start the post-navigation recentre even if the final tick did not
            // emit another native scroll event.
            driveInertia(0, true);
        });
    };
    const snapToClosestDetail = () => {
        const viewport = viewportRef.current;
        if (!viewport)
            return;
        const closestStop = milestoneRefs.current.reduce((closest, _milestone, index) => {
            const stop = detailStop(index);
            return Math.abs(stop - viewport.scrollLeft) <
                Math.abs(closest - viewport.scrollLeft)
                ? stop
                : closest;
        }, detailStop(0));
        moveTimelineTo(closestStop, {
            duration: SNAP_DURATION,
            ease: easeOutQuad,
        });
    };
    const scheduleSnap = () => {
        if (snapTimerRef.current !== null) {
            window.clearTimeout(snapTimerRef.current);
        }
        snapTimerRef.current = window.setTimeout(() => {
            snapTimerRef.current = null;
            snapToClosestDetail();
        }, SNAP_DELAY);
    };
    React.useEffect(() => {
        const viewport = viewportRef.current;
        if (!viewport)
            return;
        interruptNavigation();
        scrollbarDraggingRef.current = false;
        syncRailEndPadding();
        const index = Math.min(activeMilestoneRef.current, Math.max(0, milestones.length - 1));
        activeMilestoneRef.current = index;
        setActiveMilestone(index);
        setCopyIndex(index);
        viewport.scrollLeft = Math.max(0, detailStop(index));
        syncTimelinePosition();
        viewport.addEventListener("scroll", syncTimelinePosition, {
            passive: true,
        });
        // React wheel listeners are passive; use a cancellable native listener so
        // trackpads do not apply both native scrolling and our motion impulse.
        const wheel = (event) => {
            const delta = event.deltaX || (event.shiftKey ? event.deltaY : 0);
            if (!delta)
                return;
            event.preventDefault();
            interruptNavigation();
            const unit = event.deltaMode === 1
                ? 16
                : event.deltaMode === 2
                    ? viewport.clientWidth
                    : 1;
            viewport.scrollLeft += delta * unit;
            scheduleSnap();
        };
        viewport.addEventListener("wheel", wheel, { passive: false });
        const resize = new ResizeObserver(() => {
            syncTimelinePosition();
            if (!dragStartRef.current &&
                !scrollbarDraggingRef.current &&
                !motionRef.current.isNavigating) {
                viewport.scrollLeft = Math.max(0, detailStop(activeMilestoneRef.current));
                syncTimelinePosition();
            }
        });
        resize.observe(viewport);
        return () => {
            viewport.removeEventListener("scroll", syncTimelinePosition);
            resize.disconnect();
            viewport.removeEventListener("wheel", wheel);
            window.cancelAnimationFrame(motionRef.current.frame);
            motionRef.current.frame = 0;
            cancelNavigationRef.current?.();
            if (navigationUnlockTimerRef.current !== null) {
                window.clearTimeout(navigationUnlockTimerRef.current);
            }
            if (snapTimerRef.current !== null) {
                window.clearTimeout(snapTimerRef.current);
            }
        };
    }, [milestones]);
    const interruptNavigation = () => {
        if (snapTimerRef.current !== null) {
            window.clearTimeout(snapTimerRef.current);
            snapTimerRef.current = null;
        }
        cancelNavigationRef.current?.();
        cancelNavigationRef.current = null;
        if (navigationUnlockTimerRef.current !== null) {
            window.clearTimeout(navigationUnlockTimerRef.current);
            navigationUnlockTimerRef.current = null;
        }
        navigationLockedRef.current = false;
        setNavigationLocked(false);
        motionRef.current.isNavigating = false;
        resetInertiaEases();
    };
    const beginScrollbarDrag = () => {
        interruptNavigation();
        scrollbarDraggingRef.current = true;
        // Direct scrubbing should follow the pointer without trailing image layers.
        const motion = motionRef.current;
        window.cancelAnimationFrame(motion.frame);
        motion.frame = 0;
        motion.imageValues.fill(0);
        motion.imageTargets.fill(0);
        motion.milestoneValues.fill(0);
        motion.milestoneTargets.fill(0);
        imageMotionRefs.current.forEach((image) => {
            if (image)
                image.style.transform = "translate3d(0, 0, 0)";
        });
        milestoneRefs.current.forEach((milestone) => {
            if (milestone)
                milestone.style.transform = "translate3d(0, 0, 0)";
        });
    };
    const endScrollbarDrag = () => {
        scrollbarDraggingRef.current = false;
        syncTimelinePosition();
        snapToClosestDetail();
    };
    const moveToGroup = (index) => {
        // Ignore repeated input during the timed cooldown, not the trailing inertia.
        if (navigationLockedRef.current)
            return;
        if (snapTimerRef.current !== null)
            window.clearTimeout(snapTimerRef.current);
        const target = Math.max(0, Math.min(milestones.length - 1, index));
        moveTimelineTo(detailStop(target));
    };
    const navigate = (direction) => moveToGroup(activeMilestoneRef.current + direction);
    const endDrag = (event) => {
        if (dragStartRef.current === null)
            return;
        dragStartRef.current = null;
        setIsDragging(false);
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
        scheduleSnap();
    };
    if (milestones.length === 0)
        return null;
    const active = milestones[activeMilestone] ?? milestones[0];
    const pictures = (group) => group.gallery ??
        (group.image ? [{ src: group.image, alt: group.title }] : []);
    const gallery = lightbox
        ? pictures(milestones[lightbox.group] ?? active)
        : [];
    const lightboxImage = lightbox ? gallery[lightbox.image] : undefined;
    const changePicture = (direction) => setLightbox((current) => current
        ? {
            ...current,
            image: Math.max(0, Math.min(gallery.length - 1, current.image + direction)),
        }
        : null);
    return (_jsxs(Section, { className: cn("@container", className), tone: "default", children: [_jsx("div", { children: _jsxs("div", { className: "relative w-full", children: [_jsxs("header", { className: PAGE_GUTTER, children: [_jsx("p", { className: "type-eyebrow-lg m-0 text-foreground", "data-timeline-eyebrow": true, children: eyebrow }), _jsx("h2", { className: "type-display-section mt-2 text-primary", style: { lineHeight: 1 }, "data-timeline-title": true, children: timelineTitle })] }), _jsxs("div", { className: "relative mt-16", children: [_jsx("div", { "aria-label": "Company milestone timeline", className: "relative overflow-x-auto overflow-y-hidden overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden", onKeyDown: (event) => {
                                        if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
                                            event.preventDefault();
                                            navigate(event.key === "ArrowLeft" ? -1 : 1);
                                        }
                                    }, ref: viewportRef, role: "region", tabIndex: 0, children: _jsx("div", { className: cn("relative flex w-max touch-pan-y select-none", isDragging ? "cursor-grabbing" : "cursor-grab"), onDragStart: (event) => event.preventDefault(), onPointerCancel: endDrag, onLostPointerCapture: (event) => {
                                            // Touch starts with implicit capture on the image button.
                                            // Its capture loss bubbles when the rail takes over the drag.
                                            if (event.target === event.currentTarget)
                                                endDrag(event);
                                        }, onPointerLeave: () => {
                                            if (!draggedRef.current)
                                                dragStartRef.current = null;
                                        }, onPointerDown: (event) => {
                                            const viewport = viewportRef.current;
                                            if (!viewport || event.button !== 0)
                                                return;
                                            interruptNavigation();
                                            draggedRef.current = false;
                                            dragStartRef.current = {
                                                x: event.clientX,
                                                scrollLeft: viewport.scrollLeft,
                                            };
                                        }, onPointerMove: (event) => {
                                            const viewport = viewportRef.current;
                                            if (!viewport || !dragStartRef.current)
                                                return;
                                            const distance = event.clientX - dragStartRef.current.x;
                                            if (Math.abs(distance) > 5) {
                                                draggedRef.current = true;
                                                setIsDragging(true);
                                                event.currentTarget.setPointerCapture(event.pointerId);
                                            }
                                            if (draggedRef.current)
                                                viewport.scrollLeft =
                                                    dragStartRef.current.scrollLeft - distance;
                                        }, onPointerUp: endDrag, onClickCapture: (event) => {
                                            if (draggedRef.current) {
                                                event.preventDefault();
                                                event.stopPropagation();
                                            }
                                        }, ref: railRef, children: milestones.map((milestone, index) => {
                                            const images = pictures(milestone);
                                            return (_jsx("article", { className: "relative w-[380px] shrink-0 px-10 @min-[601px]:w-[430px]", "data-milestone-position": index + 1, ref: (node) => {
                                                    milestoneRefs.current[index] = node;
                                                }, children: _jsx("div", { ref: (node) => {
                                                        detailRefs.current[index] = node;
                                                    }, children: _jsx("div", { className: "relative h-[225.43px] w-[300px] will-change-transform @min-[601px]:h-[263px] @min-[601px]:w-[350px]", ref: (node) => {
                                                            imageMotionRefs.current[index] = node;
                                                        }, children: images[0] && (_jsx("button", { type: "button", "aria-label": `Open ${milestone.year} gallery, ${images.length} ${images.length === 1 ? "image" : "images"}`, className: "group/timeline-image relative block size-full overflow-hidden rounded-[15px] focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-4 focus-visible:outline-primary", style: { background: milestone.imageBackground }, onClick: (event) => {
                                                                if (!draggedRef.current) {
                                                                    galleryTriggerRef.current =
                                                                        event.currentTarget;
                                                                    setLightbox({ group: index, image: 0 });
                                                                }
                                                            }, children: _jsx("img", { alt: images[0].alt, className: "size-full select-none object-cover transition-transform duration-300 ease-in-out motion-safe:group-hover/timeline-image:scale-105 motion-reduce:transition-none", "data-timeline-image": true, draggable: false, src: images[0].thumbnailSrc ?? images[0].src, onError: (event) => {
                                                                    if (event.currentTarget.getAttribute("src") !==
                                                                        images[0].src)
                                                                        event.currentTarget.src = images[0].src;
                                                                }, style: milestone.imageStyle }) })) }) }) }, milestone.id ?? index));
                                        }) }) }), _jsx("div", { className: cn("mt-6", PAGE_GUTTER), children: _jsx(ArrowNav, { className: "mx-auto min-h-11 w-full max-w-[794px] gap-4", size: "large", "data-timeline-navigation": true, prevLabel: "Previous milestone group", nextLabel: "Next milestone group", prevDisabled: navigationLocked || activeMilestone === 0, nextDisabled: navigationLocked || activeMilestone === milestones.length - 1, onPrev: () => navigate(-1), onNext: () => navigate(1), children: _jsx(TimelineScrollbar, { viewportRef: viewportRef, count: milestones.length, active: activeMilestone, valueText: `${active.year}, milestone ${activeMilestone + 1} of ${milestones.length}${active.title ? `: ${active.title}` : ""}`, getRange: () => ({
                                                start: Math.max(0, detailStop(0)),
                                                end: Math.max(0, detailStop(milestones.length - 1)),
                                            }), onInteractionStart: beginScrollbarDrag, onInteractionEnd: endScrollbarDrag, onNavigate: moveToGroup }) }) }), _jsx("div", { "aria-live": "polite", className: "mt-5 flex justify-center font-sans text-[42px] font-light leading-[normal] text-[#313E48]", "data-timeline-year": true, children: _jsx(OdometerYear, { year: active.year }) }), _jsxs("div", { className: "relative mx-auto mt-4 w-[min(350px,calc(100%-40px))] text-center text-[#313E48]", style: { height: copyHeight || undefined }, children: [_jsx("div", { className: "invisible pointer-events-none absolute inset-x-0 top-0", "aria-hidden": "true", ref: copyRef, children: milestones.map((milestone, index) => (_jsxs("div", { className: "absolute inset-x-0 top-0", children: [milestone.title && (_jsx("h3", { className: "font-sans text-[20px] font-normal leading-normal mb-1", children: milestone.title })), milestone.body && (_jsx("p", { className: "font-sans text-[16px] font-light leading-[1.55]", children: milestone.body }))] }, milestone.id ?? index))) }), _jsxs("div", { "aria-live": "polite", className: "transition-opacity duration-[180ms] motion-reduce:transition-none", style: { opacity: copyVisible ? 1 : 0 }, "data-timeline-content": true, children: [milestones[copyIndex]?.title && (_jsx("h3", { className: "font-sans text-[20px] font-normal leading-normal mb-1", "data-timeline-description-header": true, children: milestones[copyIndex].title })), milestones[copyIndex]?.body && (_jsx("p", { className: "font-sans text-[16px] font-light leading-[1.55]", "data-timeline-description": true, children: milestones[copyIndex].body }))] })] })] })] }) }), _jsx(Dialog.Root, { open: lightbox !== null, onOpenChange: (open) => {
                    if (!open) {
                        lightboxSwipeRef.current = null;
                        setLightbox(null);
                    }
                }, children: _jsxs(Dialog.Portal, { children: [_jsx(Dialog.Overlay, { className: "fixed inset-0 z-[110] bg-black" }), _jsxs(Dialog.Content, { "aria-describedby": undefined, className: "fixed inset-0 z-[111] text-white outline-none", style: { touchAction: "pan-y pinch-zoom" }, onPointerDown: (event) => {
                                lightboxDraggedRef.current = false;
                                if (!event.isPrimary ||
                                    event.button !== 0 ||
                                    event.target.closest("button")) {
                                    return;
                                }
                                lightboxSwipeRef.current = {
                                    pointerId: event.pointerId,
                                    x: event.clientX,
                                    y: event.clientY,
                                };
                            }, onPointerMove: (event) => {
                                const start = lightboxSwipeRef.current;
                                if (!start || start.pointerId !== event.pointerId)
                                    return;
                                const dx = event.clientX - start.x;
                                const dy = event.clientY - start.y;
                                if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) {
                                    lightboxDraggedRef.current = true;
                                    event.currentTarget.setPointerCapture(event.pointerId);
                                }
                            }, onPointerUp: (event) => {
                                const start = lightboxSwipeRef.current;
                                if (!start || start.pointerId !== event.pointerId)
                                    return;
                                lightboxSwipeRef.current = null;
                                const dx = event.clientX - start.x;
                                const dy = event.clientY - start.y;
                                if (Math.abs(dx) >= 45 && Math.abs(dx) > Math.abs(dy)) {
                                    lightboxDraggedRef.current = true;
                                    changePicture(dx < 0 ? 1 : -1);
                                }
                                if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                                    event.currentTarget.releasePointerCapture(event.pointerId);
                                }
                            }, onPointerCancel: () => {
                                lightboxSwipeRef.current = null;
                            }, onLostPointerCapture: (event) => {
                                if (event.target === event.currentTarget) {
                                    lightboxSwipeRef.current = null;
                                }
                            }, onClickCapture: (event) => {
                                if (lightboxDraggedRef.current) {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    lightboxDraggedRef.current = false;
                                }
                            }, onClick: (event) => {
                                if (event.target === event.currentTarget)
                                    setLightbox(null);
                            }, onCloseAutoFocus: (event) => {
                                event.preventDefault();
                                galleryTriggerRef.current?.focus();
                            }, onKeyDown: (event) => {
                                if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
                                    event.preventDefault();
                                    changePicture(event.key === "ArrowLeft" ? -1 : 1);
                                }
                            }, children: [_jsxs(Dialog.Title, { className: "sr-only", children: [lightbox ? milestones[lightbox.group]?.year : "", " milestone gallery"] }), _jsx(Dialog.Close, { asChild: true, children: _jsxs("button", { type: "button", "aria-label": "Close gallery", className: "absolute right-8 top-8 z-10 h-11 w-11 cursor-pointer text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-white", children: [_jsx("span", { "aria-hidden": "true", className: "absolute left-1 top-1/2 h-0.5 w-[34px] rotate-45 rounded-sm bg-current" }), _jsx("span", { "aria-hidden": "true", className: "absolute left-1 top-1/2 h-0.5 w-[34px] -rotate-45 rounded-sm bg-current" })] }) }), _jsx("div", { className: "pointer-events-none absolute inset-x-[45px] top-[45px] bottom-[117px] grid place-items-center", "data-timeline-lightbox-image-area": true, children: lightboxImage && (_jsx(LightboxImage, { src: lightboxImage.src, alt: lightboxImage.alt })) }), _jsx(ArrowNav, { className: "absolute bottom-[45px] left-1/2 -translate-x-1/2", prevLabel: "Previous gallery image", nextLabel: "Next gallery image", prevDisabled: !lightbox?.image, nextDisabled: (lightbox?.image ?? 0) >= gallery.length - 1, onPrev: () => changePicture(-1), onNext: () => changePicture(1) })] })] }) })] }));
}
export function TimelineBlock({ data }) {
    return _jsx(Timeline, { ...data });
}
/** Legacy year content remains the first group; new groups follow in authored order. */
export function timelineFromBlock(block, resolve) {
    return {
        eyebrow: block.scalars?.eyebrow ?? "",
        timelineTitle: block.scalars?.timelineTitle ?? "Our Story",
        milestones: timelineGroupsFromBlock(block).map((group) => {
            const gallery = group.images.flatMap((image) => {
                const media = resolve(image.media.assetId
                    ? { ...image.media, cropId: ORIGINAL_RENDITION }
                    : image.media);
                // A replaced cover must never show the previous asset's cached thumbnail.
                const thumbnail = image.thumbnail?.assetId === image.media.assetId
                    ? resolve(image.thumbnail)
                    : null;
                return media
                    ? [{ src: media.url, alt: image.alt, thumbnailSrc: thumbnail?.url }]
                    : [];
            });
            return {
                id: group.id,
                year: group.year,
                title: group.title,
                body: group.description,
                tag: { label: "", value: "" },
                image: gallery[0]?.src ?? "",
                gallery,
            };
        }),
    };
}
export const timelineBlockEntry = {
    Component: TimelineBlock,
    fromBlock: timelineFromBlock,
};
//# sourceMappingURL=timeline.js.map